import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import algoliasearch from "algoliasearch/lite";
import { Configure, InstantSearch, SearchBox, RefinementList } from "react-instantsearch";

import MediaProfileCardSkeleton from "./card/MediaProfileCardSkeleton";
import InfiniteHits from "./search/InfiniteHits";
import GeoSearchByPlace from "../algolia_search/geo_search_by_place";
import SearchToolbar from "../source_directory/SearchToolbar";
import FilterPill from "../source_directory/FilterPill";
import FilterClearEffect from "../source_directory/FilterClearEffect";
import sharedAlgoliaRouter from "../source_directory/sharedAlgoliaRouter";
import useSavedOnlyFilter from "../source_directory/useSavedOnlyFilter";
import useOwnedLocationParams from "../source_directory/useOwnedLocationParams";
import useDebouncedQueryHook from "../source_directory/useDebouncedQueryHook";

const OWN_TAB = "media_profiles";

function MediaProfilesSearch({
  algoliaAppId,
  algoliaSearchKey,
  indexName,
  viewMode,
  showFilters,
  onToggleFilters,
  savedOnly,
  onToggleSavedOnly,
  onViewModeChange,
  groupCount,
  onMyGroupsClick,
  initialPathname = undefined,
}) {
  const queryHook = useDebouncedQueryHook();
  const searchClient = useMemo(() => algoliasearch(algoliaAppId, algoliaSearchKey), [algoliaAppId, algoliaSearchKey]);
  const savedFilter = useSavedOnlyFilter(savedOnly, "Id::Profile::MediaProfile");

  const { locationName, locationRadius } = useOwnedLocationParams(initialPathname, OWN_TAB);
  const [locationActive, setLocationActive] = useState(!!locationName);
  const [locationLabel, setLocationLabel] = useState(locationName ?? null);
  const [locationResetSignal, setLocationResetSignal] = useState(0);

  return (
    <div className="ais-InstantSearch">
      <InstantSearch
        indexName={indexName}
        searchClient={searchClient}
        routing={{ router: sharedAlgoliaRouter }}
        future={{ preserveSharedStateOnUnmount: true, cleanUrlOnDispose: false }}
      >
        <Configure maxValuesPerFacet={1000} filters={savedFilter ?? ""} />
        <FilterClearEffect
          showFilters={showFilters}
          onClear={() => {
            setLocationActive(false);
            setLocationLabel(null);
            setLocationResetSignal((signal) => signal + 1);
          }}
        />
        <SearchToolbar
          showFilters={showFilters}
          onToggleFilters={onToggleFilters}
          savedOnly={savedOnly}
          onToggleSavedOnly={onToggleSavedOnly}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          groupCount={groupCount}
          onMyGroupsClick={onMyGroupsClick}
        >
          <SearchBox autoFocus placeholder="Search" searchAsYouType queryHook={queryHook} />
        </SearchToolbar>

        <div
          className="sd-filter-row"
          style={{ display: showFilters ? "flex" : "none", flexWrap: "wrap", gap: 8, marginBottom: 12 }}
        >
          <FilterPill label="Publication" attribute="publications.name">
            <RefinementList attribute="publications.name" limit={10} showMore searchable />
          </FilterPill>
          <FilterPill label="Location" active={locationActive} activeText={locationLabel}>
            <div style={{ minWidth: 220 }}>
              <GeoSearchByPlace
                initialLocationName={locationName}
                initialLocationRadius={locationRadius}
                onActiveChange={setLocationActive}
                onLabelChange={setLocationLabel}
                resetSignal={locationResetSignal}
              />
            </div>
          </FilterPill>
        </div>

        <InfiniteHits MediaProfileCardSkeleton={MediaProfileCardSkeleton} viewMode={viewMode} />
      </InstantSearch>
    </div>
  );
}

MediaProfilesSearch.propTypes = {
  algoliaAppId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  indexName: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(["grid", "list"]).isRequired,
  showFilters: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  savedOnly: PropTypes.bool,
  onToggleSavedOnly: PropTypes.func.isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  groupCount: PropTypes.number,
  onMyGroupsClick: PropTypes.func,
  initialPathname: PropTypes.string,
};

export default MediaProfilesSearch;
